import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

// components
import { SbSharedLink } from '../SbSharedLink/SbSharedLink.jsx';
import { SbSharedCategoryTilesTracking } from '../SbSharedCategoryTilesTracking/SbSharedCategoryTilesTracking.tsx';
import { SbRespTilesSwiper } from '../SbRespTilesSwiper/SbRespTilesSwiper.jsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

// utils
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { createNewUriRef, getQueryParam } from '../../utils/uriUtils.ts';

//constants
import { SRC_SET_SIZES_RESP_CAROUSEL_TILE } from '../../constants/imageConstants.js';
import { SORT } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './sbRespCategoryTiles.scss';

import { type SbRespCategoryTiles_itemSearch$key } from './__generated__/SbRespCategoryTiles_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbRespCategoryTiles_itemSearch on ItemSearchQueryConnection {
        ...SbSharedCategoryTilesTracking_itemSearch
        displayUriRef
        isL1LandingPage
        categoryTiles {
            displayName
            url
            image
        }
    }
`;

type Props = {
    itemSearch: SbRespCategoryTiles_itemSearch$key;
};

export const SbRespCategoryTiles: FC<Props> = ({ itemSearch: itemSearchRef }) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { isTablet } = useServerVarsContext();
    const { displayUriRef, isL1LandingPage } = itemSearch;

    /**
     * We show 5 tiles max.
     *
     * Special case - for fashion we only show 4, but that is already handled in graphql, so we
     * don't need to handle it here.
     *
     */

    // Max tiles counter is increased for the test ENGAGE-1541.
    const maxTilesCount = isL1LandingPage ? 6 : 5;

    const tiles = itemSearch?.categoryTiles?.slice(0, maxTilesCount) || [];

    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const sort = getQueryParam(displayUriRef ?? '', SORT);

    if (!tiles.length) {
        return null;
    }

    return (
        <SbSharedCategoryTilesTracking itemSearch={itemSearch} maxTilesAllowed={maxTilesCount}>
            {({ trackClick }) => (
                <div
                    className={classnames(dibsCss.mbMedium, {
                        rowFlex: !isTablet,
                        [styles.loading]: refetchInFlight,
                    })}
                    data-tn="category-tiles"
                >
                    <SbRespTilesSwiper displayUriRef={displayUriRef} isTablet={isTablet}>
                        {tiles.map((categoryTile, index) => {
                            const { displayName, url, image } = categoryTile || {};
                            // to persist the sort query in the category tile link
                            const href = sort ? createNewUriRef(url ?? '', { sort }) : url;
                            const tileImage = image ?? '';
                            const itemContainerClasses = classnames(
                                styles.itemContainer,
                                'colXs3',
                                // For some css reason "nth-child" directive is not overwritten,
                                // so setting either one or another limit class.
                                styles.itemLimit,
                                dibsCss.flex,
                                dibsCss.flex1,
                                dibsCss.selfStretch,
                                isL1LandingPage && styles.landingPageItemContainer
                            );

                            return (
                                <SbSharedLink
                                    className={itemContainerClasses}
                                    href={href}
                                    onClick={() => trackClick(index)}
                                    key={displayName}
                                >
                                    <figure
                                        className={classnames(
                                            dibsCss.hAuto,
                                            dibsCss.relative,
                                            dibsCss.wAuto,
                                            dibsCss.flex,
                                            dibsCss.flex1,
                                            dibsCss.m0,
                                            isL1LandingPage && dibsCss.flexCol
                                        )}
                                    >
                                        <img
                                            className={classnames(
                                                dibsCss.relative,
                                                dibsCss.inset0,
                                                dibsCss.mAuto,
                                                dibsCss.objectContain,
                                                isL1LandingPage
                                                    ? [styles.landingPageImage, dibsCss.maxWFull]
                                                    : [
                                                          dibsCss.maxHXxlarge,
                                                          dibsCss.maxW33,
                                                          dibsCss.mrSmall,
                                                      ]
                                            )}
                                            src={tileImage}
                                            alt=""
                                            srcSet={getSrcsetString(tileImage)}
                                            sizes={SRC_SET_SIZES_RESP_CAROUSEL_TILE}
                                        />
                                        <figcaption
                                            className={classnames(
                                                styles.title,
                                                dibsCss.sassyFontHeaderSizeSmall,
                                                dibsCss.flex,
                                                dibsCss.wFull,
                                                dibsCss.justifyCenter,
                                                dibsCss.grow,
                                                isL1LandingPage
                                                    ? [
                                                          dibsCss.itemsStart,
                                                          dibsCss.textCenter,
                                                          dibsCss.mtSmall,
                                                      ]
                                                    : [dibsCss.itemsCenter, dibsCss.textLeft]
                                            )}
                                        >
                                            {displayName}
                                        </figcaption>
                                    </figure>
                                </SbSharedLink>
                            );
                        })}
                    </SbRespTilesSwiper>
                </div>
            )}
        </SbSharedCategoryTilesTracking>
    );
};
